/**
 * TaskPortalAttachmentService
 * Service for task attachment operations in the portal.
 * Route: api/task-portal/tasks/{taskId}/attachments
 */
import { TaskPortalBaseService } from './TaskPortalBaseService';
/**
 * TaskPortalAttachmentService
 * Handles attachment operations for the task portal.
 * Creators: Full CRUD on any attachment
 * Assignees: Create any, Edit/Delete only their own
 */
export class TaskPortalAttachmentService extends TaskPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? TaskPortalAttachmentService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? TaskPortalAttachmentService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get all attachments for a task
     * GET /api/task-portal/tasks/{taskId}/attachments
     */
    async getAttachments(taskId) {
        const response = await this.getAttachmentsRaw(taskId);
        return response.data ?? [];
    }
    getAttachmentsRaw(taskId) {
        return this.get(`${taskId}/attachments`);
    }
    /**
     * Get a specific attachment
     * GET /api/task-portal/tasks/{taskId}/attachments/{attachmentId}
     */
    async getAttachment(taskId, attachmentId) {
        const response = await this.getAttachmentRaw(taskId, attachmentId);
        if (!response.data) {
            throw new Error(`Attachment ${attachmentId} not found`);
        }
        return response.data;
    }
    getAttachmentRaw(taskId, attachmentId) {
        return this.get(`${taskId}/attachments/${attachmentId}`);
    }
    /**
     * Create a new attachment
     * POST /api/task-portal/tasks/{taskId}/attachments
     */
    async createAttachment(taskId, data) {
        const response = await this.createAttachmentRaw(taskId, data);
        if (!response.data) {
            throw new Error('Failed to create attachment');
        }
        return response.data;
    }
    createAttachmentRaw(taskId, data) {
        return this.post(`${taskId}/attachments`, data);
    }
    /**
     * Update attachment metadata
     * PUT /api/task-portal/tasks/{taskId}/attachments/{attachmentId}
     * Creators: Can update any attachment
     * Assignees: Can only update their own attachments
     */
    async updateAttachment(taskId, attachmentId, data) {
        const response = await this.updateAttachmentRaw(taskId, attachmentId, data);
        if (!response.data) {
            throw new Error('Failed to update attachment');
        }
        return response.data;
    }
    updateAttachmentRaw(taskId, attachmentId, data) {
        return this.put(`${taskId}/attachments/${attachmentId}`, data);
    }
    /**
     * Delete an attachment
     * DELETE /api/task-portal/tasks/{taskId}/attachments/{attachmentId}
     * Creators: Can delete any attachment
     * Assignees: Can only delete their own attachments
     */
    async deleteAttachment(taskId, attachmentId) {
        const response = await this.deleteAttachmentRaw(taskId, attachmentId);
        if (!response.success) {
            throw new Error('Failed to delete attachment');
        }
    }
    deleteAttachmentRaw(taskId, attachmentId) {
        return this.delete(`${taskId}/attachments/${attachmentId}`);
    }
}
TaskPortalAttachmentService.DEFAULT_SERVICE_PATH = 'tasks';
TaskPortalAttachmentService.BASE_PATH = 'api/task-portal/tasks';
//# sourceMappingURL=TaskPortalAttachmentService.js.map